import { useApiQuery } from '@tasama/api-client-query';
import { apiClient, queryClient } from '../config/api-config';

import type { UserContextResponse } from '../types/user-context.types';
import { useAuth } from '../hooks/use-auth';

/**
 * User Context API service and hooks
 * Handles fetching user-related context information for the home page
 * Uses React Query for in-memory caching + sessionStorage for persistence
 */

// Re-export for convenience
export type { UserContextResponse };

// Query key for user context
export const USER_CONTEXT_QUERY_KEY = ['home', 'user', 'context'];

/**
 * TDF-1393: a TASAMA Super Admin acting for a client must reach the same
 * admin-parity pages and controls the client's own admin reaches. The BFF
 * short-circuits admin-role guards on `isSuperAdmin` (group-auth.guard.ts)
 * and the orchestrator treats SA-acting as ADMIN_PASS_THROUGH; use this
 * helper on the FE side so route loaders and admin-only UI gates agree with
 * that back-end contract. Note: this is scoped to admin PARITY only - flags
 * that grant SA-exclusive access (e.g. canPublishCatalog) stay gated
 * separately.
 */
export const hasAdminAccess = (
  ctx: UserContextResponse | null | undefined
): boolean => ctx?.isAdmin === true || ctx?.isSuperAdmin === true;

// SessionStorage key for persistence across page refreshes
const SESSION_STORAGE_KEY = 'user-context-cache';

// Response type matching the expected API format
interface ApiResponse<T> {
  status: 'success' | 'error';
  message: string;
  data: T | null;
}

// ============================================================================
// SESSION STORAGE HELPERS (for persistence across page refreshes)
// ============================================================================

const getFromSessionStorage = (): UserContextResponse | null => {
  try {
    const cached = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!cached) return null;
    return JSON.parse(cached);
  } catch {
    return null;
  }
};

const saveToSessionStorage = (data: UserContextResponse): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
};

const clearSessionStorage = (): void => {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // ignore
  }
};

// ============================================================================
// REACT QUERY HOOK (for React components)
// ============================================================================

/**
 * Hook for fetching user context with React Query caching
 * Uses staleTime for automatic cache management (5 minutes)
 * Only fetches data when user is authenticated to prevent unauthorized API calls
 *
 * Use this in React components for automatic data fetching and caching
 */
export const useUserContextQuery = () => {
  const { isAuthenticated } = useAuth();

  return useApiQuery<UserContextResponse>(
    USER_CONTEXT_QUERY_KEY,
    '/user/context',
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 2,
      retryDelay: 1000,
      enabled: isAuthenticated, // Only fetch when user is authenticated
    } as any
  );
};

// ============================================================================
// CACHE MANAGEMENT
// ============================================================================

/**
 * Clear user context from all caches (React Query + sessionStorage)
 * Call this on logout
 */
export const clearUserContextCache = (): void => {
  queryClient.removeQueries({ queryKey: USER_CONTEXT_QUERY_KEY });
  clearSessionStorage();
};

/**
 * Invalidate user context cache (triggers refetch on next access)
 */
export const invalidateUserContextCache = (): void => {
  queryClient.invalidateQueries({ queryKey: USER_CONTEXT_QUERY_KEY });
};

// ============================================================================
// IMPERATIVE API (for router loaders and Zustand stores)
// ============================================================================

// Request deduplication - prevents concurrent API calls
let pendingRequest: Promise<ApiResponse<UserContextResponse>> | null = null;

/**
 * User context service for imperative usage in router loaders and stores
 * Uses a layered cache: React Query (in-memory) -> sessionStorage -> API
 * Implements request deduplication to prevent duplicate API calls
 */
export const userContextService = {
  /**
   * Get cached user context instantly (from React Query or sessionStorage)
   * Returns null if no cached data available - does NOT make API call
   */
  getCachedUserContext: (): UserContextResponse | null => {
    // First check React Query cache
    const queryData = queryClient.getQueryData<UserContextResponse>(
      USER_CONTEXT_QUERY_KEY
    );
    if (queryData) {
      return queryData;
    }

    // Fall back to sessionStorage
    const sessionData = getFromSessionStorage();
    if (sessionData) {
      // Hydrate React Query cache from sessionStorage
      queryClient.setQueryData(USER_CONTEXT_QUERY_KEY, sessionData);
      return sessionData;
    }

    return null;
  },

  /**
   * Fetch user context from the API
   * @param forceRefresh - Force fetch from server, bypassing all caches
   *
   * Implements request deduplication: if a request is already in progress,
   * returns the pending promise instead of making a duplicate API call.
   * Auth is enforced server-side via httpOnly cookie — a 401 is caught as an error.
   */
  getUserContext: async (
    forceRefresh: boolean = false
  ): Promise<ApiResponse<UserContextResponse>> => {
    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cachedData = userContextService.getCachedUserContext();
      if (cachedData) {
        return {
          status: 'success',
          message: 'User context retrieved from cache',
          data: cachedData,
        };
      }
    } else {
      // Clear caches if force refresh
      queryClient.removeQueries({ queryKey: USER_CONTEXT_QUERY_KEY });
    }

    // Request deduplication: return existing pending request if one is in progress
    if (pendingRequest && !forceRefresh) {
      return pendingRequest;
    }

    // Create the fetch promise
    const fetchPromise = (async (): Promise<
      ApiResponse<UserContextResponse>
    > => {
      try {
        const response =
          await apiClient.axios.get<UserContextResponse>('/user/context');

        // Update both caches
        queryClient.setQueryData(USER_CONTEXT_QUERY_KEY, response.data);
        saveToSessionStorage(response.data);

        return {
          status: 'success',
          message: 'User context retrieved successfully',
          data: response.data,
        };
      } catch (error: unknown) {
        const axiosError = error as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.message ||
          'Failed to fetch user context';
        return {
          status: 'error',
          message: errorMessage,
          data: null,
        };
      } finally {
        // Clear pending request when done
        pendingRequest = null;
      }
    })();

    // Store as pending request for deduplication
    pendingRequest = fetchPromise;

    return fetchPromise;
  },

  /**
   * Clear all user context caches
   */
  clearCache: clearUserContextCache,
};

export default userContextService;
